import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import { Input } from '@frontend/shadcn/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@frontend/shadcn/components/ui/alert-dialog';
import type { GetAllPrograms_programs } from '@frontend/app/queries/types/GetAllPrograms';
import type { GetCommunitiesQuery_communities } from '@frontend/app/queries/types/GetCommunitiesQuery';
import { TableHeaderDropdownButton } from '../../../../../Analytics/components/table-header';

type ProgramItem = GetAllPrograms_programs;
type GroupItem = GetCommunitiesQuery_communities;

interface ActionMenuComponentProps {
  handleCampaignInvite: (program: ProgramItem) => void;
  handleGroupInvite: (group: GroupItem) => void;
  handleSkipInvite: () => void;
  filteredCampaigns: ProgramItem[];
  filteredGroups: GroupItem[];
  hasSelectedRows: boolean;
  campaignSearchTerm: string;
  setCampaignSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  groupSearchTerm: string;
  setGroupSearchTerm: React.Dispatch<React.SetStateAction<string>>;

  isCampaignConfirmOpen: boolean;
  pendingCampaign: ProgramItem | null;
  proceedWithCampaignInvite: () => void;
  closeCampaignConfirmDialog: () => void;

  isGroupConfirmOpen: boolean;
  pendingGroup: GroupItem | null;
  proceedWithGroupAdd: () => void;
  closeGroupConfirmDialog: () => void;

  selectedRowsCount: number;
  inviteLoading: boolean;
  addToCommunityLoading: boolean;
}

export const ActionMenu: React.FC<ActionMenuComponentProps> = ({
  handleCampaignInvite,
  handleGroupInvite,
  handleSkipInvite,
  filteredCampaigns,
  filteredGroups,
  hasSelectedRows,
  campaignSearchTerm,
  setCampaignSearchTerm,
  groupSearchTerm,
  setGroupSearchTerm,
  isCampaignConfirmOpen,
  pendingCampaign,
  proceedWithCampaignInvite,
  closeCampaignConfirmDialog,
  isGroupConfirmOpen,
  pendingGroup,
  proceedWithGroupAdd,
  closeGroupConfirmDialog,
  selectedRowsCount,
  inviteLoading,
  addToCommunityLoading,
}) => {
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <TableHeaderDropdownButton data-dd-action-name="instagram-creator-search-actions">
            <div className="flex items-center">Actions</div>
          </TableHeaderDropdownButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              data-dd-action-name="instagram-creator-search-actions-invite"
              aria-label="Invite creator"
              className="cursor-pointer"
              disabled={!hasSelectedRows}
            >
              Invite
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <div className="p-2">
                <Input
                  placeholder="Search campaigns..."
                  value={campaignSearchTerm}
                  onChange={(e) => setCampaignSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="mb-2"
                  data-dd-action-name="instagram-creator-search-actions-search-campaigns"
                />
                {filteredCampaigns?.map((program) => (
                  <DropdownMenuItem
                    key={String(program.id)}
                    onSelect={() => {
                      handleCampaignInvite(program);
                      setCampaignSearchTerm('');
                    }}
                    className="cursor-pointer"
                    data-dd-action-name={`instagram-creator-search-actions-select-campaign-${program.id}`}
                  >
                    {program.title}
                  </DropdownMenuItem>
                ))}
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              data-dd-action-name="instagram-creator-search-actions-add-to-group"
              aria-label="Add creator to group"
              className="cursor-pointer"
              disabled={!hasSelectedRows}
            >
              Add to Group
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <div className="p-2">
                <Input
                  placeholder="Search groups..."
                  value={groupSearchTerm}
                  onChange={(e) => setGroupSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="mb-2"
                  data-dd-action-name="instagram-creator-search-actions-search-groups"
                />
                {filteredGroups?.map((group) => (
                  <DropdownMenuItem
                    key={String(group.id)}
                    onSelect={() => {
                      handleGroupInvite(group);
                      setGroupSearchTerm('');
                    }}
                    className="cursor-pointer"
                    data-dd-action-name={`instagram-creator-search-actions-select-group-${group.id}`}
                  >
                    {group.title}
                  </DropdownMenuItem>
                ))}
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem
            data-dd-action-name="instagram-creator-search-actions-skip-invite"
            aria-label="Skip invite and add to campaign"
            className="cursor-pointer"
            onClick={handleSkipInvite}
            disabled={!hasSelectedRows}
          >
            Skip Invite & Add to Campaign
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={isCampaignConfirmOpen} onOpenChange={closeCampaignConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Campaign Invite</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedRowsCount > 0 && pendingCampaign ? (
                `Are you sure you want to invite ${selectedRowsCount} creator(s) to the campaign "${pendingCampaign.title}"?`
              ) : (
                'Please select creators and a campaign.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={closeCampaignConfirmDialog}
              data-dd-action-name="instagram-creator-search-actions-cancel-campaign-invite"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={proceedWithCampaignInvite}
              disabled={!(selectedRowsCount > 0 && pendingCampaign) || inviteLoading}
              data-dd-action-name="instagram-creator-search-actions-confirm-campaign-invite"
            >
              Confirm Invite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isGroupConfirmOpen} onOpenChange={closeGroupConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Add to Group</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedRowsCount > 0 && pendingGroup ? (
                `Are you sure you want to add ${selectedRowsCount} creator(s) to the group "${pendingGroup.title}"?`
              ) : (
                'Please select creators and a group.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={closeGroupConfirmDialog}
              data-dd-action-name="instagram-creator-search-actions-cancel-group-add"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={proceedWithGroupAdd}
              disabled={!(selectedRowsCount > 0 && pendingGroup) || addToCommunityLoading}
              data-dd-action-name="instagram-creator-search-actions-confirm-group-add"
            >
              Add to Group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
